import React, { useEffect, useState } from 'react';
import { lifestyleShots } from '../data/lifestyle';

/**
 * A slow, continuous marquee of illustrative build scenes.
 *
 * The track holds the six scenes twice and translates by exactly -50%, so the
 * second copy lands where the first began and the loop never seams. Every card
 * carries its own trailing margin rather than a flex `gap`, because a gap makes
 * the halfway point land half a gap short and the loop visibly jumps.
 *
 * Pausing is done with `animation-play-state`, never by removing the animation:
 * dropping the class would reset the transform and snap back to the first card.
 *
 * The viewport itself is the control — focusable, with button semantics — so it
 * can be tapped or toggled with Space/Enter without a separate visible button.
 * It stays horizontally scrollable at all times, so a swipe works whether or
 * not the marquee is running.
 */
export function LifestyleCarousel() {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isHeld, setIsHeld] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      setPrefersReducedMotion(query.matches);
      if (query.matches) setIsPlaying(false);
    };
    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, []);
  const isRunning = isPlaying && !isHeld && !prefersReducedMotion;
  // Reduced motion gets a single static set: no duplicates to scroll past.
  const shots = prefersReducedMotion ? lifestyleShots : [...lifestyleShots, ...lifestyleShots];
  const toggle = () => setIsPlaying(playing => !playing);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      toggle();
    }
  };
  return <div className="mt-6" data-id="element-105" data-mp="1fjubboz2j7xp">
      <div role={prefersReducedMotion ? 'group' : 'button'} tabIndex={0} aria-pressed={prefersReducedMotion ? undefined : !isPlaying} aria-label={prefersReducedMotion ? 'Illustrative scenes of projects from the books' : isPlaying ? 'Pause the gallery of project scenes' : 'Play the gallery of project scenes'} onClick={prefersReducedMotion ? undefined : toggle} onKeyDown={prefersReducedMotion ? undefined : handleKeyDown} onMouseEnter={() => setIsHeld(true)} onMouseLeave={() => setIsHeld(false)} onFocus={() => setIsHeld(true)} onBlur={() => setIsHeld(false)} className="no-scrollbar -mx-5 overflow-x-auto px-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 sm:mx-0 sm:px-0" data-id="element-106" data-mp="1fjkg9mz62al5">
        <div className={`flex w-max ${prefersReducedMotion ? '' : 'marquee-track'}`} style={prefersReducedMotion ? undefined : {
        animationPlayState: isRunning ? 'running' : 'paused'
      }} data-id="element-107" data-mp="3150kx1zsboe8">
          {shots.map((shot, index) => {
          const isDuplicate = index >= lifestyleShots.length;
          return <figure key={`${shot.image}-${index}`} className="mr-4 w-[78vw] max-w-[300px] shrink-0 sm:w-[300px]" aria-hidden={isDuplicate ? 'true' : undefined} data-id="element-108" data-mp="138k5nvo137ou" data-mpi={index}>
                <img src={shot.image} alt={isDuplicate ? '' : shot.alt} className="block h-auto w-full rounded-xl border border-cream-300 bg-white object-contain" width={1456} height={1092} loading="lazy" decoding="async" data-id="element-109" data-mp="1d5yizmenngcx" />
                <figcaption className="mt-2 text-[13.5px] font-medium text-ink-800" data-id="element-110" data-mp="3nyt19m2nbbtz">
                  {shot.caption}
                </figcaption>
              </figure>;
        })}
        </div>
      </div>
    </div>;
}